import { PageContainer } from "@/components/common/page-container";
import { Allowed } from "@/components/role/allowed";
import { PayoutInvoiceContents } from "@/routes/payout/components/payout-invoice-contents";
import { PayoutMethodsTaxContents } from "@/routes/payout/components/payout-methods-contents";
import { PayoutWithdrawContents } from "@/routes/payout/components/payout-withdraw-contents";
import {
  List,
  ListItem,
  Loading,
  Typography,
  UnderlineTabs,
  UnderlineTabsContent,
  UnderlineTabsList,
  UnderlineTabsTrigger,
  useModal,
} from "@backpac/monica";
import { Suspense } from "react";
import { useSearchParams } from "react-router";
import { PayoutRevenueContents } from "./components/payout-revenue-contents";

export function usePayoutPageTabs() {
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = searchParams.get("tab") ?? "withdraw";

  return {
    tab,
    setTab: (tab: string) => setSearchParams({ tab }),
  };
}

export function PayoutPage() {
  return (
    <PageContainer
      nav={
        <PageContainer.Navigation
          title={
            <PageContainer.Title className="flex items-center gap-2">
              Payouts
              <PayoutInfo />
            </PageContainer.Title>
          }
        />
      }
    >
      <Allowed.Admin
        fallback={
          <Suspense fallback={<Loading className="h-[320px]" />}>
            <PayoutRevenueContents />
          </Suspense>
        }
      >
        <PayoutPage.Contents />
      </Allowed.Admin>
    </PageContainer>
  );
}

PayoutPage.Contents = function PayoutPageContents() {
  const { tab, setTab } = usePayoutPageTabs();

  return (
    <UnderlineTabs value={tab} onValueChange={setTab}>
      <UnderlineTabsList>
        <UnderlineTabsTrigger value="withdraw">Withdraw</UnderlineTabsTrigger>
        <UnderlineTabsTrigger value="history">History</UnderlineTabsTrigger>
        <UnderlineTabsTrigger value="methods">Payout methods & Tax</UnderlineTabsTrigger>
      </UnderlineTabsList>
      <UnderlineTabsContent value="withdraw">
        <Suspense fallback={<Loading className="h-[320px]" />}>
          <PayoutWithdrawContents />
        </Suspense>
      </UnderlineTabsContent>
      <UnderlineTabsContent value="history">
        <Suspense fallback={<Loading className="h-[320px]" />}>
          <PayoutInvoiceContents />
        </Suspense>
      </UnderlineTabsContent>
      <UnderlineTabsContent value="methods">
        <Suspense fallback={<Loading className="h-[320px]" />}>
          <PayoutMethodsTaxContents />
        </Suspense>
      </UnderlineTabsContent>
    </UnderlineTabs>
  );
};

function PayoutInfo() {
  const { open } = useModal();

  return (
    <button
      className="inline-flex"
      type="button"
      onClick={() =>
        open({
          title: "Understanding Your Earnings",
          titleClassName: "!font-inter text-lg font-semibold text-sm",
          className: "max-w-[480px] gap-3",
          children: (
            <List>
              <ListItem>
                <Typography size="paragraphXs" color="textSecondary">
                  You can request a payout once your balance reaches $30.
                </Typography>
              </ListItem>
              <Typography size="xs" weight={600} color="textSecondary" className="mb-1.5 mt-3">
                Campaign
              </Typography>
              <ListItem>
                <Typography size="paragraphXs" color="textSecondary">
                  Music Campaign fees are added to your balance according to each campaign’s individual settlement
                  schedule.
                </Typography>
              </ListItem>
              <Typography size="xs" weight={600} color="textSecondary" className="mb-1.5 mt-3">
                BackPac BGM
              </Typography>
              <ListItem>
                <Typography size="paragraphXs" color="textSecondary">
                  The revenue shown on your dashboard is an estimate and may differ slightly from your final payout.
                  Your finalized revenue reflects the actual amount settled.
                </Typography>
              </ListItem>
              <ListItem>
                <Typography size="paragraphXs" color="textSecondary">
                  Differences between estimated and finalized revenue can be due to exchange rate updates, data
                  verification, or adjustments for invalid revenue.
                </Typography>
              </ListItem>
              <ListItem>
                <Typography size="paragraphXs" color="textSecondary">
                  Music revenue is finalized at the end of the month following the month of activity. For example,
                  revenue from December will be finalized at the end of January.
                </Typography>
              </ListItem>
            </List>
          ),
        })
      }
    >
      <i className="i-bp-info inline-block size-4" />
    </button>
  );
}
